<script lang="ts">
  import { onMount } from "svelte"
  import { xhr, toast } from "$app/utils"
  import { Pulse } from "svelte-loading-spinners"

  import { ClipboardCheck, ChevronRight, CirclePlus } from "lucide-svelte"
  import Modal from "$app/components/Modal.svelte"
  import NewGoal from "../Goals/NewGoal.svelte"
  import VisionView from "./VisionView.svelte"
  import GoalDetail from "../Goals/GoalDetail.svelte"
  import GoalsBoxLt from "./GoalsBoxLt.svelte"
  import GoalsBoxAnnual from "./GoalsBoxAnnual.svelte"
  import GoalsBoxDept from "./GoalsBoxDept.svelte"

  import { sortBy } from "lodash"
  import Honeybadger from "$app/utils/honeybadger"
  import empty_img from "@images/onboarding/empty.png"
  import { appStore } from "$app/store.svelte"
  import { FREE_DEPARTMENT_MAX } from "$app/constants"

  interface Props {
    editable?: boolean
    annual_vision?: any
    quarterly_vision?: any
  }

  let {
    editable = $bindable(false),
    annual_vision = $bindable(null),
    quarterly_vision = $bindable(null)
  }: Props = $props()

  let plan_year: number = appStore.plan_year

  let rootGoalId = $state(null)
  let ltGoals = $state([])
  let annualGoals = $state([])
  let quarterlyGoals = $state([])
  let options = $state({})
  let periods = $state({})

  let isLoading = $state(false)
  let hideCompleted = $state(false)

  let openNewGoalModal = $state(false)
  let openGoalModal = $state(false)
  let openGoal = $state(null)
  let openVisionsModal = $state(false)
  let headings = $state(appStore.headings)

  let goalOptions = $state({} as GoalType)
  function openNewGoalHandler(options = {}) {
    openNewGoalModal = true
    goalOptions = options as GoalType
  }

  let departments: DepartmentType[] = $derived(
    appStore.subscription.subscription_type === "free"
      ? appStore.departments.slice(0, FREE_DEPARTMENT_MAX)
      : appStore.departments
  )

  function openGoalModalHandler(goal) {
    openGoal = goal
    openGoalModal = true
  }

  function openVisionsModalHandler() {
    openVisionsModal = true
  }

  type respType = {
    data: {
      root_goal_id: number
      longterm_goals: GoalType[]
      annual_goals: GoalType[]
      quarterly_goals: GoalType[]
      options: { [key: string]: number }
      periods: { [key: string]: number[] }
      plan: PlanType
      hide_completed: boolean
      annual_vision: any
      quarterly_vision: any
    }
  }

  async function getGoals(quarter = null, year = null) {
    let params = ""
    if (quarter && year) {
      params = `?quarter=${quarter}&year=${year}`
    }
    isLoading = true

    try {
      const resp = (await xhr.get(
        `/plans/${appStore.plan.id}.json${params}`
      )) as respType

      rootGoalId = resp.data.root_goal_id
      ltGoals = sortBy(resp.data.longterm_goals, "parent_position")
      annualGoals = sortBy(resp.data.annual_goals, "assignee_position")
      quarterlyGoals = sortBy(resp.data.quarterly_goals, "assignee_position")

      options = resp.data.options
      periods = resp.data.periods
      hideCompleted = resp.data.hide_completed
      annual_vision = resp.data.annual_vision
      quarterly_vision = resp.data.quarterly_vision
    } catch (e) {
      console.error("Error loading plan goals:", e)
      if (typeof Honeybadger.notify !== "undefined") {
        Honeybadger.notify(e)
      }
      toast("There was an error loading the plan goals", "error")
    } finally {
      isLoading = false
    }
  }

  function updateHeadings(data) {
    headings = data.headings
  }

  function updateGoalsPeriod(data) {
    plan_year = data.year
    getGoals(data.quarter, data.year)
  }

  function addedNewGoal(goal) {
    openNewGoalModal = false

    if (goal.type === "LongTermGoal") {
      ltGoals = [...ltGoals, goal]
      ltGoals = sortBy(ltGoals, "parent_position")
    } else if (goal.type === "AnnualGoal") {
      annualGoals = [...annualGoals, goal]
      annualGoals = sortBy(annualGoals, "assignee_position")
    } else if (goal.type === "QuarterlyGoal") {
      quarterlyGoals = [...quarterlyGoals, goal]
      quarterlyGoals = sortBy(quarterlyGoals, "assignee_position")
    }
  }

  function updateGoal(data) {
    let goal = data as GoalType
    if (goal.type === "LongTermGoal") {
      const goalIndex = ltGoals.findIndex((g) => g.id === goal.id)
      ltGoals[goalIndex] = goal
      ltGoals = sortBy(ltGoals, "parent_position")
    } else if (goal.type === "AnnualGoal") {
      const goalIndex = annualGoals.findIndex((g) => g.id === goal.id)
      annualGoals[goalIndex] = goal
      annualGoals = sortBy(annualGoals, "assignee_position")
    } else {
      const idx = quarterlyGoals.findIndex((g) => g.id === goal.id)
      quarterlyGoals[idx] = goal
      quarterlyGoals = sortBy(quarterlyGoals, "assignee_position")
    }
  }

  function updateLtGoals(data) {
    data.forEach((goal) => {
      const goalIndex = ltGoals.findIndex((g) => g.id === goal.id)
      ltGoals[goalIndex] = goal
    })
    ltGoals = sortBy(ltGoals, "parent_position")
  }

  function updateAnnualGoals(data) {
    data.forEach((goal) => {
      const goalIndex = annualGoals.findIndex((g) => g.id === goal.id)
      annualGoals[goalIndex] = goal
    })
    annualGoals = sortBy(annualGoals, "assignee_position")
  }

  function updateQuarterlyGoals(data) {
    data.forEach((goal) => {
      const goalIndex = quarterlyGoals.findIndex((g) => g.id === goal.id)
      quarterlyGoals[goalIndex] = goal
    })

    quarterlyGoals = sortBy(quarterlyGoals, "assignee_position")
  }

  async function updateHideCompleted(data) {
    hideCompleted = data

    try {
      const resp = await xhr.patch(
        `/plans/${appStore.plan.id}/update_hide_completed`,
        {
          hide_completed: hideCompleted
        }
      )
    } catch (e) {
      Honeybadger.notify(e)
    }
    annualGoals = sortBy(annualGoals, "assignee_position")
  }

  const onboardingCompleted = () => {
    if (
      appStore.onboarding?.completed_onboarding ||
      appStore.onboarding?.onboarding_steps.task
    ) {
      return true
    }

    return false
  }

  const activeStep = () => {
    if (
      appStore.onboarding?.completed_onboarding ||
      appStore.onboarding?.onboarding_steps.task ||
      (appStore.onboarding?.skipped_onboarding &&
        appStore.onboarding?.current_onboarding_step.match(
          /^(goal_overview|long_term_goal|annual_goal|quarterly_goal|task|goal_review|review)$/
        ))
    ) {
      return true
    }

    return false
  }

  const quarterTitle = () => {
    if (options["quarter"] == -1) {
      return "All Quarters"
    } else {
      return `Q${options["quarter"]}`
    }
  }

  onMount(async () => {
    getGoals()
  })
</script>

{#if isLoading}
  <div class="py-12 flex justify-center w-full">
    <Pulse size="240" color="#cdcdcd" unit="px" duration="1s" />
  </div>
{:else if !onboardingCompleted() && activeStep()}
  <div class="py-12 flex flex-col justify-center w-full bg-white shadow mt-6">
    <div class="flex items-center justify-center">
      <h3 class="leading-6 font-bold text-black text-center">Goals</h3>
    </div>
    <div class="flex flex-col justify-center items-center mt-4">
      <ClipboardCheck class="size-24 text-gray-500" />

      <a
        href={`/plans/${appStore.plan.id}/goals`}
        class="mt-6 border border-gray-200 rounded-lg py-3 px-4 flex bg-green-600 hover:bg-green-500 hover:text-white text-gray-50 items-center justify-center"
      >
        <div class="text-center text-base mr-1">
          Start setting up your goals
        </div>
        <ChevronRight class="size-5" />
      </a>
    </div>
    <div class="mt-4 flex justify-center"></div>
  </div>
{:else if !onboardingCompleted()}
  <div class="mt-4 bg-gray-400 opacity-30 overflow-hidden shadow py-8">
    <div class="flex items-center justify-center">
      <h3 class="leading-6 font-bold text-black text-center">Goals</h3>
    </div>

    <div class="w-full">
      <div class="flex flex-col justify-center items-center mt-4">
        <img src={empty_img} class="w-28 h-auto" alt="No Goals" />
        <p class="text-base mt-4">No Goals Yet</p>
      </div>
    </div>
  </div>
{:else if onboardingCompleted() && ltGoals.length === 0}
  <div class="py-12 flex flex-col justify-center w-full">
    <p class="text-xl font-bold text-center">Your goals will show up here</p>
    <div class="flex justify-center mt-4">
      <button
        class="btn btn-outline flex text-lg"
        role="menuitem"
        onclick={() => openNewGoalHandler({ type: "LongTermGoal" })}
      >
        <CirclePlus class="size-5 text-gray-500" />
        <div class="ml-2">Create Your First Goal</div>
      </button>
    </div>
  </div>
{:else}
  <div
    class="w-full grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4"
    id="goal-boxes"
  >
    {#key ltGoals}
      <GoalsBoxLt
        goals={ltGoals}
        {rootGoalId}
        {headings}
        {editable}
        onopenModal={openGoalModalHandler}
        onupdateGoals={updateLtGoals}
        onupdateHeadings={updateHeadings}
      />
    {/key}
    {#key annualGoals}
      <GoalsBoxAnnual
        goals={annualGoals}
        {options}
        {periods}
        {hideCompleted}
        {headings}
        {editable}
        showQuarterSelector={true}
        onupdateGoalsPeriod={updateGoalsPeriod}
        onopenModal={openGoalModalHandler}
        onopenNewGoalModal={() => openNewGoalHandler({ type: "AnnualGoal" })}
        onopenVisionsModal={openVisionsModalHandler}
        onupdateHideCompleted={updateHideCompleted}
        onupdateGoals={updateAnnualGoals}
        onupdateHeadings={updateHeadings}
      />
    {/key}

    {#key quarterlyGoals}
      {#key hideCompleted}
        {#each departments as department}
          <GoalsBoxDept
            goals={quarterlyGoals}
            {department}
            {hideCompleted}
            {editable}
            title="{department.name} Goals - {quarterTitle()}"
            quarterlyGoalCount={appStore.quarterly_goals.length}
            onopenModal={openGoalModalHandler}
            onupdateGoals={updateQuarterlyGoals}
            onopenNewGoalModal={() =>
              openNewGoalHandler({
                type: "QuarterlyGoal",
                assignee_type: "Department",
                assignee_id: department.id
              })}
          />
        {/each}
      {/key}
    {/key}

    {#if appStore.departments.length > FREE_DEPARTMENT_MAX && appStore.subscription.subscription_type === "free"}
      <div
        class="col-span-1 lg:col-span-2 flex items-center justify-center py-2"
      >
        <a
          href={`/plans/${appStore.plan.id}/admin`}
          class="text-sm text-gray-500 bg-yellow-50 py-1 px-3 border border-gray-300 rounded shadow-sm hover:underline"
        >
          Upgrade your subscription to view more than {FREE_DEPARTMENT_MAX}
          departments.
        </a>
      </div>
    {/if}
  </div>
{/if}

<Modal
  bind:open={openNewGoalModal}
  closeButton={true}
  closeOnEsc={false}
  closeOnOuterClick={false}
>
  <NewGoal goal={goalOptions} oncreate={addedNewGoal} />
</Modal>

<Modal
  bind:open={openGoalModal}
  closeButton={true}
  closeOnEsc={false}
  closeOnOuterClick={false}
  large={true}
>
  <GoalDetail bind:goal={openGoal} />
</Modal>

<Modal
  bind:open={openVisionsModal}
  closeButton={true}
  closeOnEsc={false}
  closeOnOuterClick={false}
  large={true}
>
  <VisionView {options} {annual_vision} {quarterly_vision} />
</Modal>
